import { MemberType, memberSchema } from '@/api/members'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
    memberQueryOptions,
    useUpdateMemberMutation,
} from '../../api/memberQueryOptions'

import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, useWatch } from 'react-hook-form'

import {
    Grid,
    IconButton,
    IconButtonProps,
    styled
} from '@mui/material'

import { fetchCity } from '@/api/address'
import { fetchDetailPermissionsQueryOptions } from '@/api/permissionsQueryOptions'
import CityAutocomplete from '@/components/CityAutocomplete'
import FormDebug from '@/components/FormDebug'
import useResponsive from '@/components/hooks/useResponsive'
import LoadProgress from '@/components/LoadProgress'
import ScoregCheckbox from '@/components/mui/ScoregCheckbox'
import ScoregDatePicker from '@/components/mui/ScoregDatePicker'
import ScoregTextField from '@/components/mui/ScoregTextField'
import PostcodeAutocomplete from '@/components/PostcodeAutocomplete'
import SelectSex from '@/components/select/SelectSex'
import StreetAutocomplete from '@/components/StreetAutocomplete'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import dayjs from 'dayjs'
import { t } from 'i18next'
import { enqueueSnackbar } from 'notistack'
import React from 'react'

interface ExpandMoreProps extends IconButtonProps {
    expand: string
}

const ExpandMore = styled((props: ExpandMoreProps) => {
    const { ...other } = props
    return <IconButton {...other} />
})(({ theme, expand }) => ({
    transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
    }),
}))

export const Route = createFileRoute(
    '/_auth/member_/$memberUuid/main',
)({
    component: MemberComponent,
})

function MemberComponent() {
    const params = Route.useParams()
    const memberQuery = useSuspenseQuery(memberQueryOptions(params.memberUuid))
    const member = memberQuery.data

    const [memberData, setMemberData] = React.useState<MemberType | undefined>(
        undefined,
    )
    const responsive = useResponsive()

    const updateMemberMutation = useUpdateMemberMutation(member.uuid)

    const {
        control,
        setValue,
        register,
        handleSubmit,
        formState: { errors, isDirty, isValid },
        getValues,
    } = useForm<MemberType>({
        resolver: yupResolver<MemberType, any, any>(memberSchema),
        mode: 'onBlur',
        shouldFocusError: true,
    })

    const watchPostcode = useWatch({ control, name: 'postcode' })
    React.useEffect(() => {
        if (watchPostcode && watchPostcode.length == 4) {
            const cityPromise = fetchCity(watchPostcode || '')
            cityPromise.then((city: string) => {
                console.log(city)
                if (city && city.length > 0) {
                    setValue('city', city)
                }
            })
        }

    }, [watchPostcode, setValue])

    const [phoneMobileWarning, setPhoneMobileWarning] = React.useState('')
    const [phoneLineWarning, setPhoneLineWarning] = React.useState('')

    const watchPhoneMobile = useWatch({ control, name: 'phoneMobile' })
    const watchPhoneLine = useWatch({ control, name: 'phoneLine' })

    React.useEffect(() => {
        const regex = new RegExp('^[+]{0,1}[0-9]+[/ ]{0,1}[ 0-9]+[ -]{0,1}[ 0-9]+$')
        const valid = watchPhoneMobile && (regex.test(watchPhoneMobile || '') || watchPhoneMobile === '')
        if (!valid && watchPhoneMobile) {
            setPhoneMobileWarning(t('error.phoneSyntax'))
        } else {
            setPhoneMobileWarning('')
        }
    }, [watchPhoneMobile])

    React.useEffect(() => {
        const regex = new RegExp('^[+]{0,1}[0-9]+[/ ]{0,1}[ 0-9]+[ -]{0,1}[ 0-9]+$')
        const valid = watchPhoneLine && (regex.test(watchPhoneLine || '') || watchPhoneLine === '')
        if (!valid && watchPhoneLine) {
            setPhoneLineWarning(t('error.phoneSyntax'))
        } else {
            setPhoneLineWarning('')
        }
    }, [watchPhoneLine])

    const onSubmit = (data: MemberType) => {
        // console.log('onSubmit')

        // console.log(JSON.stringify(memberData || member))
        // console.log(JSON.stringify({...member, ...data}))
        // console.log(JSON.stringify(data))

        const isChanged =
            JSON.stringify(memberData || member) !=
            JSON.stringify({ ...member, ...data })

        // console.log(isChanged)

        if (isValid && isChanged) {
            // console.log('update')
            updateMemberMutation.mutate(
                { ...member, ...data },
                {
                    onError(error) {
                        enqueueSnackbar(t('error.message', { message: error.message }), {
                            variant: 'error',
                        })
                    },
                    onSuccess() {
                        enqueueSnackbar(t('action.changed', { entity: t('entity.data') }), {
                            variant: 'success',
                        })
                    },
                },
            )
            setMemberData({ ...member, ...data })
        }
    }

    const onError = () => {
        console.log('error')
        console.log(JSON.stringify(errors))
    }

    const [expanded, setExpanded] = React.useState(responsive.minLarge)

    const handleExpandClick = () => {
        setExpanded(!expanded)
    }

    const permissionsQuery = useSuspenseQuery(
        fetchDetailPermissionsQueryOptions('member', params.memberUuid, [
            'address',
            'phone',
            'email',
        ]),
    )

    if (permissionsQuery.isLoading || permissionsQuery.isFetching) {
        return <LoadProgress />
    }
    if (memberQuery.isLoading || memberQuery.isFetching) {
        return <LoadProgress />
    }

    const permissions = permissionsQuery.data

    const readAddress =
        permissions &&
        permissions.filter(
            (p) => p.detail === 'address' && p.permissions.some((a) => a === 'read'),
        ).length > 0
    const readPhone =
        permissions &&
        permissions.filter(
            (p) => p.detail === 'phone' && p.permissions.some((a) => a === 'read'),
        ).length > 0
    const readEmail =
        permissions &&
        permissions.filter(
            (p) => p.detail === 'email' && p.permissions.some((a) => a === 'read'),
        ).length > 0

    if (memberQuery.isLoading || memberQuery.isFetching) {
        return <LoadProgress />
    }

    const writeable = member.permissions
        ? member.permissions.some((perm) => perm === 'write')
        : false

    return (
        <form onBlur={handleSubmit(onSubmit, onError)}>
            <Grid container spacing={1}>
                <Grid
                    size={{
                        xs: 12,
                        md: 12,
                        lg: 12
                    }}>
                    <div>
                        <Grid container spacing={1}>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6,
                                    lg: 6
                                }}>
                                <input
                                    type="hidden"
                                    {...register('uuid')}
                                    defaultValue={member.uuid}
                                />
                                <input
                                    type="hidden"
                                    {...register('scoutId')}
                                    defaultValue={member.scoutId}
                                />
                                <ScoregTextField
                                    defaultValue={member.firstname}
                                    {...register('firstname')}
                                    disabled={!writeable}
                                    label={t('property.member.firstname')}
                                    helperText={errors.firstname?.message}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6,
                                    lg: 6
                                }}>
                                <ScoregTextField
                                    defaultValue={member.lastname}
                                    {...register('lastname')}
                                    disabled={!writeable}
                                    required={true}
                                    label={t('property.member.lastname')}
                                    helperText={errors.lastname?.message}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6,
                                    lg: 6
                                }}>
                                <SelectSex
                                    control={control}
                                    name="sex"
                                    defaultValue={member.sex}
                                    disabled={!writeable}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6,
                                    lg: 6
                                }}>
                                <ScoregDatePicker
                                    name="birthdate"
                                    defaultValue={
                                        member.birthdate ? dayjs(member.birthdate) : undefined
                                    }
                                    disabled={!writeable}
                                    control={control}
                                    label={t('property.member.birthdate')}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6,
                                    lg: 6
                                }}></Grid>
                        </Grid>
                    </div>
                    {readAddress ? (
                        <div>
                            <Grid container spacing={1}>
                                <Grid
                                    size={{
                                        xs: 6,
                                        md: 3,
                                        lg: 2
                                    }}>
                                    <ScoregTextField
                                        defaultValue={member.country}
                                        {...register('country')}
                                        disabled={!writeable}
                                        label={t('property.member.country')}
                                        helperText={errors.country?.message}
                                    />
                                </Grid>
                            </Grid>

                            <Grid container spacing={1}>
                                <Grid
                                    size={{
                                        xs: 4,
                                        md: 2,
                                        lg: 2
                                    }}>
                                    <PostcodeAutocomplete
                                        control={control}
                                        setValue={setValue}
                                        name="postcode"
                                        defaultValue={member.postcode}
                                        disabled={!writeable}
                                        errors={errors}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 8,
                                        md: 4,
                                        lg: 4
                                    }}>
                                    <CityAutocomplete
                                        control={control}
                                        setValue={setValue}
                                        name="city"
                                        defaultValue={member.city}
                                        disabled={!writeable}
                                        getPostcode={() => {
                                            return getValues('postcode')
                                        }}
                                        errors={errors}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        md: 6,
                                        lg: 6
                                    }}>
                                    <StreetAutocomplete
                                        control={control}
                                        setValue={setValue}
                                        name="street"
                                        defaultValue={member.street}
                                        disabled={!writeable}
                                        getPostcode={() => {
                                            return getValues('postcode')
                                        }}
                                        getCity={() => {
                                            return getValues('city')
                                        }}
                                        errors={errors}
                                    />
                                </Grid>
                            </Grid>
                        </div>
                    ) : (
                        <></>
                    )}
                    {readEmail ? (
                        <div>
                            <Grid container spacing={2}>
                                <Grid
                                    size={{
                                        xs: 12,
                                        md: 6,
                                        lg: 4
                                    }}>
                                    <ScoregTextField
                                        defaultValue={member.emailPrimary}
                                        {...register('emailPrimary')}
                                        disabled={!writeable}
                                        label={t('property.member.emailPrimary')}
                                        aria-invalid={errors.emailPrimary ? 'true' : 'false'}
                                        helperText={errors.emailPrimary?.message}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        md: 6,
                                        lg: 4
                                    }}>
                                    <ScoregTextField
                                        defaultValue={member.emailSecondary}
                                        {...register('emailSecondary')}
                                        disabled={!writeable}
                                        label={t('property.member.emailSecondary')}
                                        aria-invalid={errors.emailSecondary ? 'true' : 'false'}
                                        helperText={errors.emailSecondary?.message}
                                    />
                                </Grid>
                            </Grid>
                        </div>
                    ) : (
                        <></>
                    )}
                    {readPhone ? (
                        <div>
                            <Grid container spacing={2}>
                                <Grid
                                    size={{
                                        xs: 12,
                                        md: 6,
                                        lg: 4
                                    }}>
                                    <ScoregTextField
                                        defaultValue={member.phoneLine}
                                        {...register('phoneLine')}
                                        onKeyDown={(e) => {
                                            if (e.code === 'Space' || e.code === 'Backspace' || e.code === 'Delete' || e.code === 'ArrowRight' || e.code === 'ArrowLeft') {
                                                return true
                                            }
                                            if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
                                                return true
                                            }
                                            if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
                                                return true
                                            }
                                            if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
                                                return true
                                            }
                                            if (!/^[-+/ 0-9]+$/.test(e.key)) {
                                                e.preventDefault()
                                            }
                                        }}
                                        disabled={!writeable}
                                        label={t('property.member.phoneLine')}
                                        helperText={errors.phoneLine?.message || phoneLineWarning}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        md: 6,
                                        lg: 4
                                    }}>
                                    <ScoregTextField
                                        defaultValue={member.phoneMobile}
                                        {...register('phoneMobile')}
                                        onKeyDown={(e) => {
                                            if (e.code === 'Space' || e.code === 'Backspace' || e.code === 'Delete' || e.code === 'ArrowRight' || e.code === 'ArrowLeft') {
                                                return true
                                            }
                                            if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
                                                return true
                                            }
                                            if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
                                                return true
                                            }
                                            if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
                                                return true
                                            }
                                            if (!/^[-+/ 0-9]+$/.test(e.key)) {
                                                e.preventDefault()
                                            }
                                        }}
                                        disabled={!writeable}
                                        label={t('property.member.phoneMobile')}
                                        helperText={errors.phoneMobile?.message || phoneMobileWarning}
                                    />
                                </Grid>
                            </Grid>
                        </div>
                    ) : (
                        <></>
                    )}

                    <div>
                        <Grid container spacing={1}>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 12,
                                    lg: 8
                                }}>
                                <ExpandMore
                                    expand={expanded.toString()}
                                    onClick={handleExpandClick}
                                    aria-expanded={expanded}
                                    aria-label="show more"
                                >
                                    {expanded ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                                </ExpandMore>
                                {t('label.member.moreData')}
                            </Grid>
                        </Grid>
                        {expanded ? (
                            <>

                                <Grid container spacing={2}>
                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6,
                                            lg: 4
                                        }}>
                                        <ScoregCheckbox
                                            defaultValue={member.handicap}
                                            {...register('handicap')}
                                            control={control}
                                            disabled={!writeable}
                                            label={t('property.member.handicap')}
                                        />
                                    </Grid>
                                </Grid>

                                <Grid container spacing={1}>
                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6,
                                            lg: 4
                                        }}>
                                        <ScoregTextField
                                            defaultValue={member.titlePrefix}
                                            {...register('titlePrefix')}
                                            disabled={!writeable}
                                            label={t('property.member.titlePrefix')}
                                            helperText={errors.titlePrefix?.message}
                                        />
                                    </Grid>
                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6,
                                            lg: 4
                                        }}>
                                        <ScoregTextField
                                            defaultValue={member.titlePostfix}
                                            {...register('titlePostfix')}
                                            disabled={!writeable}
                                            label={t('property.member.titlePostfix')}
                                            helperText={errors.titlePostfix?.message}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container spacing={1}>
                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6,
                                            lg: 4
                                        }}>
                                        <ScoregTextField
                                            defaultValue={member.secondname}
                                            {...register('secondname')}
                                            disabled={!writeable}
                                            label={t('property.member.secondname')}
                                            helperText={errors.secondname?.message}
                                        />
                                    </Grid>
                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6,
                                            lg: 4
                                        }}>
                                        <ScoregTextField
                                            defaultValue={member.nickname}
                                            {...register('nickname')}
                                            disabled={!writeable}
                                            label={t('property.member.nickname')}
                                            helperText={errors.nickname?.message}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container spacing={1}>
                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6,
                                            lg: 4
                                        }}>
                                        <ScoregTextField
                                            defaultValue={member.birthyear}
                                            {...register('birthyear')}
                                            disabled={!writeable}
                                            label={t('property.member.birthyear')}
                                            helperText={errors.birthyear?.message}
                                        />
                                    </Grid>
                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6,
                                            lg: 4
                                        }}>
                                        <ScoregTextField
                                            defaultValue={member.nameOfBirth}
                                            {...register('nameOfBirth')}
                                            disabled={!writeable}
                                            label={t('property.member.nameOfBirth')}
                                            helperText={errors.nameOfBirth?.message}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container spacing={1}>
                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6,
                                            lg: 4
                                        }}>
                                        <ScoregDatePicker
                                            name="scoutPromise"
                                            defaultValue={
                                                member.scoutPromise ? dayjs(member.scoutPromise) : null
                                            }
                                            disabled={!writeable}
                                            control={control}
                                            label={t('property.member.scoutPromise')}
                                        />
                                    </Grid>
                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6,
                                            lg: 4
                                        }}>
                                        <ScoregDatePicker
                                            name="scoutPromiseSmall"
                                            defaultValue={
                                                member.scoutPromiseSmall ? dayjs(member.scoutPromiseSmall) : null
                                            }
                                            disabled={!writeable}
                                            control={control}
                                            label={t('property.member.scoutPromiseSmall')}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container spacing={1}>
                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6,
                                            lg: 4
                                        }}>
                                        <ScoregTextField
                                            defaultValue={member.addressaddition1}
                                            {...register('addressaddition1')}
                                            disabled={!writeable}
                                            label={t('property.member.addressaddition1')}
                                            helperText={errors.addressaddition1?.message}
                                        />
                                    </Grid>
                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6,
                                            lg: 4
                                        }}>
                                        <ScoregTextField
                                            defaultValue={member.addressaddition2}
                                            {...register('addressaddition2')}
                                            disabled={!writeable}
                                            label={t('property.member.addressaddition2')}
                                            helperText={errors.addressaddition2?.message}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container spacing={1}>
                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6,
                                            lg: 4
                                        }}>
                                        <ScoregTextField
                                            defaultValue={member.profession}
                                            {...register('profession')}
                                            disabled={!writeable}
                                            label={t('property.member.profession')}
                                            helperText={errors.profession?.message}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container spacing={1}>
                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6,
                                            lg: 4
                                        }}>
                                        <ScoregDatePicker
                                            name="joinDate"
                                            defaultValue={
                                                member.joinDate ? dayjs(member.joinDate) : null
                                            }
                                            disabled={!writeable}
                                            control={control}
                                            label={t('property.member.joinDate')}
                                        />
                                    </Grid>
                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6,
                                            lg: 4
                                        }}>
                                        <ScoregDatePicker
                                            name="dayOfRetirement"
                                            defaultValue={
                                                member.dayOfRetirement ? dayjs(member.dayOfRetirement) : null
                                            }
                                            disabled={!writeable}
                                            control={control}
                                            label={t('property.member.dayOfRetirement')}
                                            helperText={t('helperText.member.dayOfRetirement')}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container spacing={1}>
                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6,
                                            lg: 4
                                        }}>
                                        <ScoregDatePicker
                                            name="dayOfDeath"
                                            defaultValue={
                                                member.dayOfDeath ? dayjs(member.dayOfDeath) : undefined
                                            }
                                            disabled={!writeable}
                                            control={control}
                                            label={t('property.member.dayOfDeath')}
                                        />
                                    </Grid>
                                </Grid>

                                {t('visibility.member.taxId') === 'true' &&
                                    <Grid container spacing={1}>
                                        <Grid
                                            size={{
                                                xs: 12,
                                                md: 6,
                                                lg: 4
                                            }}>
                                            <ScoregTextField
                                                defaultValue={member.taxId}
                                                {...register('taxId')}
                                                disabled={!writeable}
                                                label={t('property.member.taxId')}
                                                helperText={errors.taxId?.message}
                                            />
                                        </Grid>
                                    </Grid>
                                }
                            </>
                        ) : (
                            <></>
                        )}
                    </div>
                </Grid>
            </Grid>
            <FormDebug
                errors={errors}
                dirty={isDirty}
                valid={isValid}
                value={member}
            />
            {import.meta.env.VITE_DEBUG === 'true' &&
                <DevTool control={control} />
            }
        </form>
    );
}
